/**
 * Number Field Component
 *
 * Specialized input component for numeric values with theme support.
 * Supports prefixes, validation, and proper number formatting.
 *
 * @module components/ui/NumberField
 */

import React from 'react';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/utils/cn';

/**
 * Props for the NumberField component
 */
export interface NumberFieldProps {
  /** Label for the input field */
  label: string;
  /** Current numeric value */
  value: string | number;
  /** Callback when value changes */
  onChange: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Minimum allowed value */
  min?: string | number;
  /** Maximum allowed value */
  max?: string | number;
  /** Step increment for number input */
  step?: string;
  /** Prefix to display (e.g., "$" for currency) */
  prefix?: string;
  /** Additional CSS classes for the container */
  className?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Validation state */
  state?: 'default' | 'error' | 'warning' | 'success';
  /** Error message to display */
  error?: string;
  /** Warning message to display */
  warning?: string;
  /** Success message to display */
  success?: string;
  /** Helper text to display below input */
  helperText?: string;
}

/**
 * NumberField Component
 *
 * Renders a labeled number input field with theme support and optional prefix.
 * Optimized for numeric input with proper validation attributes.
 */
export const NumberField: React.FC<NumberFieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
  required = false,
  min,
  max,
  step,
  prefix,
  className,
  disabled = false,
  state = 'default',
  error,
  warning,
  success,
  helperText,
}) => {
  const { isDark } = useTheme();

  // Generate unique IDs for accessibility
  const inputId = `number-field-${Math.random().toString(36).substr(2, 9)}`;
  const errorId = `${inputId}-error`;
  const warningId = `${inputId}-warning`;
  const successId = `${inputId}-success`;
  const helperId = `${inputId}-helper`;

  // Determine which message to show (priority: error > warning > success > helper)
  const message = error || warning || success || helperText;
  const messageId = error ? errorId : warning ? warningId : success ? successId : helperId;

  // Build aria-describedby
  const describedBy = message ? messageId : undefined;

  // State classes for border and focus
  const stateClasses = {
    default: cn(
      'focus:ring-primary-500/50',
      isDark
        ? 'border-gray-500 focus:border-primary-400'
        : 'border-gray-500 focus:border-primary-500'
    ),
    error: cn(
      'focus:ring-red-500/50',
      isDark ? 'border-red-400 focus:border-red-400' : 'border-red-500 focus:border-red-500'
    ),
    warning: cn(
      'focus:ring-yellow-500/50',
      isDark ? 'border-yellow-400 focus:border-yellow-400' : 'border-yellow-500 focus:border-yellow-500'
    ),
    success: cn(
      'focus:ring-green-500/50',
      isDark ? 'border-green-400 focus:border-green-400' : 'border-green-500 focus:border-green-500'
    )
  };

  return (
    <div className={cn('space-y-1', className)}>
      {label && (
        <label
          htmlFor={inputId}
          className={cn(
            'block text-sm font-medium',
            isDark ? 'text-secondary-300' : 'text-secondary-700',
            required && 'after:content-["*"] after:ml-0.5 after:text-red-500'
          )}
        >
          {label}
        </label>
      )}
      <div className="relative">
        {prefix && (
          <span className={cn(
            'absolute left-3 top-1/2 -translate-y-1/2 text-md pointer-events-none',
            isDark ? 'text-secondary-100' : 'text-neutral-900'
          )}>
            {prefix}
          </span>
        )}
        <input
          id={inputId}
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          aria-describedby={describedBy}
          aria-invalid={state === 'error' ? 'true' : 'false'}
          aria-required={required}
          className={cn(
            'w-full px-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 transition-all duration-200',
            prefix && 'pl-8',
            disabled && 'opacity-50 cursor-not-allowed',
            stateClasses[state],
            isDark
              ? 'bg-secondary-800 text-secondary-100 placeholder:text-secondary-400'
              : 'bg-white text-secondary-900 placeholder:text-secondary-200'
          )}
        />
      </div>

      {/* Message */}
      {message && (
        <p
          id={messageId}
          className={cn(
            'text-sm',
            error && (isDark ? 'text-red-400' : 'text-red-600'),
            warning && (isDark ? 'text-yellow-400' : 'text-yellow-600'),
            success && (isDark ? 'text-green-400' : 'text-green-600'),
            !error && !warning && !success && (isDark ? 'text-secondary-400' : 'text-secondary-600')
          )}
          role={error ? 'alert' : undefined}
          aria-live={error ? 'polite' : undefined}
        >
          {message}
        </p>
      )}
    </div>
  );
};

export default NumberField;